import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { AppLayout } from '../../components/layout/AppLayout';
import theme from '../../theme';

const { width } = Dimensions.get('window');

interface ImportExportCategory {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  route: string;
  sections: {
    id: string;
    name: string;
    description: string;
    status: 'ready' | 'processing' | 'complete' | 'error';
  }[];
}

export const ImportExportMainScreen = () => {
  const navigation = useNavigation();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories: ImportExportCategory[] = [
    {
      id: 'excel_imports',
      title: 'Excel & CSV Upload',
      description: 'Upload data from Excel files with automatic column mapping',
      icon: 'document-text',
      color: '#10B981',
      route: '',
      sections: [
        {
          id: 'vehicles_excel',
          name: 'Vehicle Data Upload',
          description: 'Upload vehicle information from Excel/CSV files',
          status: 'ready',
        },
      ],
    },
    {
      id: 'transactions',
      title: 'CSV Import with Verification',
      description: 'Advanced import system with data verification and reconciliation',
      icon: 'shield-checkmark',
      color: theme.colors.success,
      route: '',
      sections: [
        {
          id: 'careem',
          name: 'Careem Import',
          description: 'Import Careem captain earnings with verification',
          status: 'ready',
        },
        {
          id: 'uber',
          name: 'Uber Import',
          description: 'Import Uber driver earnings with verification',
          status: 'ready',
        },
        {
          id: 'salik',
          name: 'Salik Import',
          description: 'Import Salik toll charges with verification',
          status: 'ready',
        },
        {
          id: 'fines',
          name: 'Traffic Fines',
          description: 'Import traffic violation fines with verification',
          status: 'ready',
        },
      ],
    },
    {
      id: 'databases',
      title: 'Database Export',
      description: 'Export core database information for backup and analysis',
      icon: 'server',
      color: theme.colors.primary,
      route: '',
      sections: [
        {
          id: 'vehicles',
          name: 'Vehicle Export',
          description: 'Export vehicle details, registration, and specifications',
          status: 'ready',
        },
      ],
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready':
        return theme.colors.gray400;
      case 'processing':
        return theme.colors.warning;
      case 'complete':
        return theme.colors.success;
      case 'error':
        return theme.colors.error;
      default:
        return theme.colors.gray400;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ready':
        return 'radio-button-off';
      case 'processing':
        return 'hourglass';
      case 'complete':
        return 'checkmark-circle';
      case 'error':
        return 'close-circle';
      default:
        return 'radio-button-off';
    }
  };

  const handleSectionPress = (categoryId: string, sectionId: string) => {
    // Route Excel upload types to new Excel upload screens
    if (sectionId === 'vehicles_excel') {
      navigation.navigate('VehicleExcelUpload' as never);
      return;
    }
    
    // Route all import types to the new unified CSV Import screen
    if (['careem', 'uber', 'salik', 'fines'].includes(sectionId)) {
      navigation.navigate('CSVImport' as never);
      return;
    }
    
    // Keep existing export routes
    const routeMap: { [key: string]: string } = {
      'vehicles': 'VehicleExport',
    };

    const route = routeMap[sectionId];
    if (route) {
      navigation.navigate(route as never);
    }
  };

  const CategoryCard = ({ category }: { category: ImportExportCategory }) => (
    <View style={styles.categoryCard}>
      <View style={styles.categoryHeader}>
        <View style={[styles.categoryIcon, { backgroundColor: category.color + '15' }]}>
          <Ionicons name={category.icon as any} size={24} color={category.color} />
        </View>
        <View style={styles.categoryInfo}>
          <Text style={styles.categoryTitle}>{category.title}</Text>
          <Text style={styles.categoryDescription}>{category.description}</Text>
        </View>
      </View>

      <View style={styles.sectionsContainer}>
        {category.sections.map((section) => (
          <TouchableOpacity
            key={section.id}
            style={styles.sectionItem}
            onPress={() => handleSectionPress(category.id, section.id)}
          >
            <View style={styles.sectionInfo}>
              <Text style={styles.sectionName}>{section.name}</Text>
              <Text style={styles.sectionDescription}>{section.description}</Text>
            </View>
            <View style={styles.sectionStatus}>
              <Ionicons 
                name={getStatusIcon(section.status)} 
                size={20} 
                color={getStatusColor(section.status)} 
              />
              <Ionicons name="chevron-forward" size={16} color={theme.colors.gray400} />
            </View>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  return (
    <AppLayout title="Import & Export">
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerBadge}>
              <Ionicons name="shield-checkmark" size={16} color={theme.colors.success} />
              <Text style={styles.badgeText}>New Verification System</Text>
            </View>
            <Text style={styles.title}>Data Import & Export</Text>
            <Text style={styles.subtitle}>
              Advanced CSV import with data verification, reconciliation, and export tools
            </Text>
            
            {/* Driver Sync Info Card */}
            <TouchableOpacity 
              style={styles.infoCard}
              onPress={() => navigation.navigate('Settings' as never)}
            >
              <View style={styles.infoIcon}>
                <Ionicons name="sync" size={20} color="#EF4444" />
              </View>
              <View style={styles.infoContent}>
                <Text style={styles.infoTitle}>Looking for Driver Import?</Text>
                <Text style={styles.infoText}>
                  Driver CSV sync has moved to Settings → Driver Sync
                </Text>
              </View>
              <Ionicons name="arrow-forward" size={20} color="#EF4444" />
            </TouchableOpacity>
          </View>

          {/* Categories */}
          <View style={styles.categoriesContainer}>
            {categories.map((category) => (
              <CategoryCard key={category.id} category={category} />
            ))}
          </View>

          {/* Recent Activity */}
          <View style={styles.activitySection}>
            <Text style={styles.activityTitle}>Recent Activity</Text>
            <View style={styles.activityCard}>
              <View style={styles.activityItem}>
                <Ionicons name="cloud-upload" size={20} color={theme.colors.success} />
                <View style={styles.activityInfo}>
                  <Text style={styles.activityText}>Last Uber import completed</Text>
                  <Text style={styles.activityTime}>2 hours ago • 222 transactions</Text>
                </View>
              </View>
              <View style={styles.activityItem}>
                <Ionicons name="cloud-upload" size={20} color={theme.colors.success} />
                <View style={styles.activityInfo}>
                  <Text style={styles.activityText}>Careem data synchronized</Text>
                  <Text style={styles.activityTime}>5 hours ago • 217 transactions</Text>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollView: {
    flex: 1,
  },
  header: {
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  headerBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.success + '15',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    alignSelf: 'flex-start',
    marginBottom: theme.spacing.md,
    gap: 6,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.success,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  subtitle: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
    lineHeight: 20,
  },
  infoCard: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing.md,
    padding: theme.spacing.md,
    backgroundColor: '#EF4444' + '10',
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: '#EF4444' + '30',
    gap: theme.spacing.sm,
  },
  infoIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#EF4444' + '20',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoContent: {
    flex: 1,
  },
  infoTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: '#EF4444',
    marginBottom: 2,
  },
  infoText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    lineHeight: 16,
  },
  categoriesContainer: {
    padding: theme.spacing.lg,
    gap: theme.spacing.lg,
  },
  categoryCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  categoryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.lg,
  },
  categoryIcon: {
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  categoryInfo: {
    flex: 1,
  },
  categoryTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  categoryDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  sectionsContainer: {
    gap: theme.spacing.xs,
  },
  sectionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.gray50,
  },
  sectionInfo: {
    flex: 1,
  },
  sectionName: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  sectionDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  sectionStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  activitySection: {
    padding: theme.spacing.lg,
    paddingTop: 0,
  },
  activityTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  activityCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing.sm,
  },
  activityInfo: {
    marginLeft: theme.spacing.md,
  },
  activityText: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  activityTime: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
});
